import React, { ReactNode, useEffect } from 'react'
import { alpha, Dialog, DialogContent, Slide, Box, useTheme, SxProps, Theme } from '@mui/material'
import { designTokens } from '@/theme'
import { TransitionProps } from '@mui/material/transitions'
import { useVerticalDrag } from '@/hooks/useVerticalDrag'
import { useBottomSheetSnap } from '@/hooks/useBottomSheetSnap'

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

interface BottomSheetProps {
  open: boolean
  onClose?: () => void
  children: ReactNode
  /** Optional header content */
  header?: ReactNode
  /** Optional footer content (actions) */
  footer?: ReactNode
  /** Maximum height as percentage of viewport (default: 90) */
  maxHeight?: number
  /** If true, prevents dismissing by clicking backdrop or swiping (default: false) */
  disableDismiss?: boolean
  /** Optional paper styling override (e.g. background gradient) */
  paperSx?: SxProps<Theme>
  /** Optional header styling override */
  headerSx?: SxProps<Theme>
  /** Optional footer styling override */
  footerSx?: SxProps<Theme>
  /** Optional scroll-body (DialogContent) styling override */
  contentSx?: SxProps<Theme>
  /** If true, always show drag handle (default: true when disableDismiss, false otherwise) */
  showHandle?: boolean
  /** Optional snap points (e.g. ['50%', '90%'] or [420, 760]) */
  snapPoints?: Array<string | number>
  /** Initial snap index in sorted snap points */
  initialSnap?: number
  /** When true, skip slide/fade transitions (prefers-reduced-motion). */
  reduceMotion?: boolean
  /** Optional accessible name id for the dialog (MUI Dialog). */
  ariaLabelledBy?: string
  /** Optional accessible description id for the dialog (MUI Dialog). */
  ariaDescribedBy?: string
  /** When true, MUI will not restore focus to the opener after close. */
  disableRestoreFocus?: boolean
}

/**
 * Mobile-first bottom sheet component for confirmations and actions.
 * Slides up from bottom, full width, rounded top corners.
 */
export default function BottomSheet({
  open,
  onClose,
  children,
  header,
  footer,
  maxHeight = 90,
  disableDismiss = false,
  paperSx,
  headerSx,
  footerSx,
  contentSx,
  showHandle = !disableDismiss,
  snapPoints,
  initialSnap = 0,
  reduceMotion = false,
  ariaLabelledBy,
  ariaDescribedBy,
  disableRestoreFocus = false,
}: BottomSheetProps) {
  const theme = useTheme()
  const { hasSnapPoints, maxSnapHeight, initialTranslateY, snapTranslatePoints } = useBottomSheetSnap({
    snapPoints,
    initialSnap,
  })

  useEffect(() => {
    if (!open) return
    const prevOverflow = document.body.style.overflow
    const prevOverscroll = document.documentElement.style.overscrollBehavior
    const prevBodyTouchAction = document.body.style.touchAction
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overscrollBehavior = 'none'
    document.body.style.touchAction = 'none'
    return () => {
      document.body.style.overflow = prevOverflow
      document.documentElement.style.overscrollBehavior = prevOverscroll
      document.body.style.touchAction = prevBodyTouchAction
    }
  }, [open])

  const { bind, translateY, isDragging } = useVerticalDrag({
    onClose: () => {
      if (!disableDismiss) onClose?.()
    },
    threshold: 120,
    disableDismiss,
    snapTranslatePoints,
    initialTranslateY,
    closeThreshold: hasSnapPoints && snapTranslatePoints
      ? snapTranslatePoints[snapTranslatePoints.length - 1] * 0.75
      : undefined,
    velocityCloseThreshold: 0.95,
    velocitySnapThreshold: 0.22,
  })

  const handleClose = disableDismiss ? undefined : (onClose || undefined)

  const mobileTopRadius = `${designTokens.shape.borderRadius.xl}px ${designTokens.shape.borderRadius.xl}px 0 0`
  const footerBaseSx = {
    px: 2,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 0,
    [theme.breakpoints.up(640)]: {
      borderRadius: `0 0 ${designTokens.shape.borderRadius.xl}px ${designTokens.shape.borderRadius.xl}px`,
    },
  }

  const basePaperStyles = {
    overflow: 'visible' as const,
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    margin: 0,
    maxHeight: hasSnapPoints && maxSnapHeight != null ? `${Math.round(maxSnapHeight)}px` : `${maxHeight}vh`,
    height: hasSnapPoints && maxSnapHeight != null ? `${Math.round(maxSnapHeight)}px` : 'auto',
    width: '100%',
    maxWidth: '100%',
    borderRadius: mobileTopRadius,
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[24],
    [theme.breakpoints.up(640)]: {
      position: 'relative' as const,
      margin: 'auto',
      maxWidth: 520,
      width: '100%',
      bottom: 'auto',
      left: 'auto',
      right: 'auto',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: `${designTokens.shape.borderRadius.xl}px`,
      boxShadow: theme.shadows[24],
    },
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={reduceMotion ? undefined : Transition}
      transitionDuration={reduceMotion ? 0 : undefined}
      keepMounted
      fullWidth
      maxWidth={false}
      disableEscapeKeyDown={disableDismiss}
      disableRestoreFocus={disableRestoreFocus}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      sx={{
        '& .MuiDialog-container': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          width: '100%',
          padding: 0,
          margin: 0,
          [theme.breakpoints.up(640)]: {
            alignItems: 'center',
            padding: 2,
          },
        },
        '& .MuiDialog-paper': {
          margin: 0,
          width: '100%',
          maxWidth: '100%',
          [theme.breakpoints.up(640)]: {
            margin: 'auto',
            maxWidth: 520,
          },
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.35)',
          backdropFilter: 'blur(2px)',
          ...(disableDismiss && {
            pointerEvents: 'none',
          }),
        },
      }}
      PaperProps={{
        sx: (paperSx ? [basePaperStyles, paperSx] : basePaperStyles) as SxProps<Theme>,
      }}
    >
      <Box
        {...(!disableDismiss ? bind : {})}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          // Fill the bounded Paper height so the inner scroll container has a
          // definite parent height. Without this, the DialogContent's
          // `flex: 1; min-height: 0; overflow-y: auto` has nothing to bound it
          // and the offer list overflows instead of scrolling internally.
          flex: 1,
          minHeight: 0,
          width: '100%',
          transform: `translateY(${translateY}px)`,
          transition:
            isDragging || reduceMotion
              ? 'none'
              : 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: reduceMotion ? 'auto' : 'transform',
        }}
      >
        {/* Drag handle indicator */}
        {showHandle && (
          <Box
            data-drag-handle="true"
            role="presentation"
            aria-hidden
            sx={{
              width: 36,
              height: 4,
              borderRadius: 999,
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              margin: '8px auto',
              cursor: disableDismiss ? 'default' : 'grab',
              touchAction: 'none',
              '&:active': {
                cursor: disableDismiss ? 'default' : 'grabbing',
              },
            }}
          />
        )}
        
        {header && (
          <Box
            sx={
              (headerSx
                ? [{ px: 2, pt: 1, pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }, headerSx]
                : { px: 2, pt: 1, pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }) as SxProps<Theme>
            }
          >
            {header}
          </Box>
        )}

        <DialogContent
          data-bottom-sheet-scroll="true"
          sx={
            (contentSx
              ? [
                  {
                    px: 2,
                    py: 2,
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorY: 'contain',
                    touchAction: 'pan-y',
                    '&::-webkit-scrollbar': {
                      width: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.divider,
                      borderRadius: '2px',
                    },
                  },
                  contentSx,
                ]
              : {
                  px: 2,
                  py: 2,
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehaviorY: 'contain',
                  touchAction: 'pan-y',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.divider,
                    borderRadius: '2px',
                  },
                }) as SxProps<Theme>
          }
        >
          {children}
        </DialogContent>

        {footer && (
          <Box
            sx={
              (footerSx
                ? [{ ...footerBaseSx, py: 2 }, footerSx]
                : {
                    ...footerBaseSx,
                    pt: 2,
                    // Home-indicator / gesture-bar clearance on mobile sheets.
                    pb: 'max(16px, env(safe-area-inset-bottom, 0px))',
                  }) as SxProps<Theme>
            }
          >
            {footer}
          </Box>
        )}
      </Box>
    </Dialog>
  )
}
